/**
 * Plugin Galerie d'Images - Version modulaire strictement identique au legacy
 */

import React, { useState, useEffect, useMemo } from 'react';
import { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { API_CONFIG } from '../config/api.config';
// ✅ ÉTAPE 1 : Importer le composant de traduction
import AutoTranslateText from '../components/AutoTranslateText';
import { useFileManager } from '../hooks/useFileManager';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../hooks/useTinyMceApiKey';

export {}; // Force module mode

const TINYMCE_TEXT_INIT = withTabLink({
  menubar: false,
  toolbar_mode: 'wrap',
  toolbar_items_size: 'small',
  resize: true,
  height: 260,
  min_height: 200,
  plugins: [
    'advlist',
    'autolink',
    'lists',
    'charmap',
    'searchreplace',
    'wordcount'
  ],
  placeholder: 'Saisissez votre texte…',
  content_style: `
    body { font-family: Arial, sans-serif; font-size: 14px; padding: 4px; }
    img[style*="float: left"] { margin: 0 12px 12px 0; }
    img[style*="float: right"] { margin: 0 0 12px 12px; }
  `,
  toolbar:
    'undo redo | fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | ' +
    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat'
});

// Composant de barre d'outils de formatage
const TextFormattingToolbar: React.FC<{
  style: any;
  onStyleChange: (newStyle: any) => void;
  // ✅ CORRECTION : Accepter un composant React pour le label
  label: React.ReactNode;
}> = ({ style, onStyleChange, label }) => {
  const fonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana', 
    'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Courier New'
  ];

  const updateStyle = (property: string, value: any) => {
    onStyleChange({
      ...style,
      [property]: value
    });
  };

  return (
    <div className="text-formatting-toolbar" style={{ 
      marginTop: '3px', 
      padding: '5px', 
      backgroundColor: '#f8f9fa', 
      borderRadius: '3px',
      border: '1px solid #dee2e6',
      fontSize: '10px'
    }}>
      <div style={{ fontSize: '9px', color: '#6c757d', marginBottom: '3px', fontWeight: 'bold' }}>
        {label}
      </div>
      
      {/* Première ligne : Gras, Italique, Souligné, Alignement */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '3px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn btn-sm ${style?.fontWeight === 'bold' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => updateStyle('fontWeight', style?.fontWeight === 'bold' ? 'normal' : 'bold')}
          style={{ fontSize: '8px', padding: '1px 4px', minWidth: '20px' }}
          title="Gras"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${style?.fontStyle === 'italic' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => updateStyle('fontStyle', style?.fontStyle === 'italic' ? 'normal' : 'italic')}
          style={{ fontSize: '8px', padding: '1px 4px', minWidth: '20px' }}
          title="Italique"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${style?.textDecoration === 'underline' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => updateStyle('textDecoration', style?.textDecoration === 'underline' ? 'none' : 'underline')}
          style={{ fontSize: '8px', padding: '1px 4px', minWidth: '20px' }}
          title="Souligné"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${style?.textAlign === 'left' || !style?.textAlign ? 'btn-secondary' : 'btn-outline-secondary'}`}
          onClick={() => updateStyle('textAlign', 'left')}
          style={{ fontSize: '8px', padding: '1px 4px', minWidth: '20px' }}
          title="Gauche"
        >
          ⬅️
        </button>
        <button
          type="button"
          className={`btn btn-sm ${style?.textAlign === 'center' ? 'btn-secondary' : 'btn-outline-secondary'}`}
          onClick={() => updateStyle('textAlign', 'center')}
          style={{ fontSize: '8px', padding: '1px 4px', minWidth: '20px' }}
          title="Centre"
        >
          ↔️
        </button>
        <button
          type="button"
          className={`btn btn-sm ${style?.textAlign === 'right' ? 'btn-secondary' : 'btn-outline-secondary'}`}
          onClick={() => updateStyle('textAlign', 'right')}
          style={{ fontSize: '8px', padding: '1px 4px', minWidth: '20px' }}
          title="Droite"
        >
          ➡️
        </button>
      </div>

      {/* Deuxième ligne : Police, taille et couleur */}
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={style?.fontFamily || 'Arial'}
          onChange={(e) => updateStyle('fontFamily', e.target.value)}
          style={{ fontSize: '8px', padding: '1px', flex: '1', minWidth: '60px', maxWidth: '80px' }}
          title="Police"
        >
          {fonts.map(font => (
            <option key={font} value={font}>{font}</option>
          ))}
        </select>
        <input
          type="number"
          value={style?.fontSize || 14}
          onChange={(e) => updateStyle('fontSize', parseInt(e.target.value))}
          min="8"
          max="72"
          style={{ fontSize: '8px', padding: '1px', width: '30px' }}
          title="Taille"
        />
        <span style={{ fontSize: '8px', color: '#6c757d' }}>px</span>
        <input
          type="color"
          value={style?.color || '#000000'}
          onChange={(e) => updateStyle('color', e.target.value)}
          style={{ width: '20px', height: '16px', border: 'none', borderRadius: '2px', cursor: 'pointer' }}
          title="Couleur"
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => updateStyle('color', '#000000')}
          style={{ fontSize: '7px', padding: '1px 3px' }}
          title="Reset couleur"
        >
          R
        </button>
      </div>
    </div>
  );
};

export interface ImageGalleryConfig {
  id?: string;
  title: string;
  description?: string;
  // ✅ AJOUT : Styles globaux (Titre et Description)
  titleGlobalStyle?: {
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    textDecoration?: 'none' | 'underline';
    textAlign?: 'left' | 'center' | 'right';
    fontFamily?: string;
    fontSize?: number;
    color?: string;
  };
  descriptionGlobalStyle?: {
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    textDecoration?: 'none' | 'underline';
    textAlign?: 'left' | 'center' | 'right';
    fontFamily?: string;
    fontSize?: number;
    color?: string;
  };

  images: Array<{
    url: string;
    title: string;
    description?: string;
    alt?: string;
    titleStyle?: {
      fontWeight?: 'normal' | 'bold';
      fontStyle?: 'normal' | 'italic';
      textDecoration?: 'none' | 'underline';
      textAlign?: 'left' | 'center' | 'right';
      fontFamily?: string;
      fontSize?: number;
      color?: string;
    };
    descriptionStyle?: {
      fontWeight?: 'normal' | 'bold';
      fontStyle?: 'normal' | 'italic';
      textDecoration?: 'none' | 'underline';
      textAlign?: 'left' | 'center' | 'right';
      fontFamily?: string;
      fontSize?: number;
      color?: string;
    };
  }>;
  displayMode: 'grid' | 'carousel' | 'masonry';
  showDisplayModeSelector?: boolean;
  columns: number;
  showTitles: boolean;
  showDescriptions: boolean;
  lightbox: boolean;
  gallerySize: 'small' | 'medium' | 'large';
  imageHeight: number;
  transitionEffect: 'slide' | 'fade' | 'zoom' | 'flip' | 'cube';
  transitionDuration: number;
  autoplay: boolean;
  autoplayDelay: number;
  backgroundColor?: string;
  // ✅ AJOUTS : Nouvelles propriétés de style
  borderRadius?: number;
  responsive?: boolean;
  
  showTitle?: boolean;
  showDescription?: boolean;
  showIndicators?: boolean;
}

// Composant de rendu avec effets corrigés
const ImageGalleryRenderer: React.FC<PluginPreviewProps> = ({ config }) => {
  const galleryConfig = config as ImageGalleryConfig;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const configuredDisplayMode = galleryConfig.displayMode || 'grid';
  const [displayMode, setDisplayMode] = useState(configuredDisplayMode);
  const activeDisplayMode = galleryConfig.showDisplayModeSelector ? displayMode : configuredDisplayMode;
  
  // ID unique pour ce carousel - stable avec une base fixe
  const carouselId = useMemo(() => {
    return `carousel-${Math.abs(JSON.stringify(galleryConfig).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0))}`;
  }, [galleryConfig.title, galleryConfig.images?.length]);
  
  // Navigation avec gestion des transitions
  const goToNext = () => {
    if (!galleryConfig.images || galleryConfig.images.length === 0 || isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % galleryConfig.images.length);
    setTimeout(() => setIsTransitioning(false), galleryConfig.transitionDuration || 1000);
  };
  
  const goToPrev = () => {
    if (!galleryConfig.images || galleryConfig.images.length === 0 || isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => 
      prev === 0 ? galleryConfig.images.length - 1 : prev - 1
    );
    setTimeout(() => setIsTransitioning(false), galleryConfig.transitionDuration || 1000);
  };
  
  const goToSlide = (index: number) => {
    if (index === currentSlide || isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), galleryConfig.transitionDuration || 1000);
  };
  
  // Auto-avancement - DÉMARRE IMMÉDIATEMENT et gère les transitions
  useEffect(() => {
    if (
      galleryConfig.autoplay && 
      activeDisplayMode === 'carousel' && 
      galleryConfig.images && 
      galleryConfig.images.length > 1 &&
      !isTransitioning
    ) {
      const interval = setInterval(() => {
        goToNext();
      }, galleryConfig.autoplayDelay || 3000);
      return () => clearInterval(interval);
    }
  }, [galleryConfig.autoplay, galleryConfig.autoplayDelay, activeDisplayMode, galleryConfig.images, currentSlide, isTransitioning]);

  useEffect(() => {
    setDisplayMode(configuredDisplayMode);
  }, [configuredDisplayMode]);
  
  // Styles CSS dynamiques avec effets corrigés
  useEffect(() => {
    const styleId = `gallery-styles-${carouselId}`;
    let existingStyle = document.getElementById(styleId);
    
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    
    const duration = (galleryConfig.transitionDuration || 1000) / 1000;
    
    // ✅ UTILISATION DE LA CONFIG POUR L'ARRONDI
    const radius = galleryConfig.borderRadius !== undefined ? galleryConfig.borderRadius : 8;

    style.textContent = `
      .carousel-${carouselId} {
        position: relative;
        width: 100%;
        height: ${galleryConfig.imageHeight || 400}px;
        overflow: hidden;
        border-radius: ${radius}px; /* ✅ Dynamique */
        background: ${galleryConfig.backgroundColor && galleryConfig.backgroundColor !== '' ? galleryConfig.backgroundColor : 'transparent'};
        /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); Supprimé pour éviter double ombre avec le conteneur principal */
      }
      
      .carousel-${carouselId} .slide-container {
        position: relative;
        width: 100%;
        height: 100%;
      }
      
      .carousel-${carouselId} .slide {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all ${duration}s cubic-bezier(0.4, 0, 0.2, 1);
        opacity: 0;
        z-index: 1;
      }
      
      .carousel-${carouselId} .slide.active {
        opacity: 1;
        z-index: 2;
      }
      
      /* Effet Fade (défaut) */
      .carousel-${carouselId}.effect-fade .slide {
        opacity: 0;
        transform: scale(1.05);
      }
      .carousel-${carouselId}.effect-fade .slide.active {
        opacity: 1;
        transform: scale(1);
      }
      
      /* Effet Slide */
      .carousel-${carouselId}.effect-slide .slide {
        transform: translateX(100%);
        opacity: 1;
      }
      .carousel-${carouselId}.effect-slide .slide.active {
        transform: translateX(0);
        opacity: 1;
      }
      .carousel-${carouselId}.effect-slide .slide.prev {
        transform: translateX(-100%);
        opacity: 1;
      }
      
      /* Effet Zoom */
      .carousel-${carouselId}.effect-zoom .slide {
        transform: scale(0.5);
        opacity: 0;
      }
      .carousel-${carouselId}.effect-zoom .slide.active {
        transform: scale(1);
        opacity: 1;
      }
      
      /* Effet Flip */
      .carousel-${carouselId}.effect-flip {
        perspective: 1000px;
      }
      .carousel-${carouselId}.effect-flip .slide {
        transform: rotateY(90deg);
        opacity: 0;
        backface-visibility: hidden;
      }
      .carousel-${carouselId}.effect-flip .slide.active {
        transform: rotateY(0deg);
        opacity: 1;
      }
      
      /* Effet Cube */
      .carousel-${carouselId}.effect-cube {
        perspective: 1200px;
      }
      .carousel-${carouselId}.effect-cube .slide {
        transform: rotateX(-90deg);
        transform-origin: center bottom;
        opacity: 0;
      }
      .carousel-${carouselId}.effect-cube .slide.active {
        transform: rotateX(0deg);
        opacity: 1;
      }
      
      .carousel-${carouselId} .slide img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 4px;
      }
      
      /* Contrôles */
      .carousel-${carouselId} .nav-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: rgba(0, 0, 0, 0.7);
        color: white;
        border: none;
        border-radius: 50%;
        width: 50px;
        height: 50px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        z-index: 10;
        transition: all 0.3s ease;
        backdrop-filter: blur(5px);
      }
      
      .carousel-${carouselId} .nav-btn:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.9);
        transform: translateY(-50%) scale(1.1);
      }
      
      .carousel-${carouselId} .nav-btn:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      
      .carousel-${carouselId} .nav-btn.prev {
        left: 15px;
      }
      
      .carousel-${carouselId} .nav-btn.next {
        right: 15px;
      }
      
      /* Indicateurs */
      .carousel-${carouselId} .dots {
        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 10px;
        z-index: 10;
        background: rgba(0, 0, 0, 0.5);
        padding: 8px 12px;
        border-radius: 20px;
        backdrop-filter: blur(5px);
      }
      
      .carousel-${carouselId} .dots button {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.3);
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .carousel-${carouselId} .dots button.active {
        background: white;
        transform: scale(1.3);
        border-color: white;
      }
      
      .carousel-${carouselId} .dots button:hover:not(.active) {
        background: rgba(255, 255, 255, 0.6);
        transform: scale(1.1);
      }
      
      /* Légendes */
      .carousel-${carouselId} .caption {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
        color: white;
        padding: 30px 20px 20px;
        text-align: center;
      }
      
      .carousel-${carouselId} .caption h5 {
        margin: 0 0 8px 0;
        font-size: 1.2rem;
        font-weight: 600;
      }
      
      .carousel-${carouselId} .caption p {
        margin: 0;
        font-size: 0.95rem;
        opacity: 0.9;
      }
      
      /* Responsive */
      @media (max-width: 768px) {
        .carousel-${carouselId} .nav-btn {
          width: 40px;
          height: 40px;
          font-size: 16px;
        }
        
        .carousel-${carouselId} .nav-btn.prev {
          left: 10px;
        }
        
        .carousel-${carouselId} .nav-btn.next {
          right: 10px;
        }
        
        .carousel-${carouselId} .caption {
          padding: 20px 15px 15px;
        }
        
        .carousel-${carouselId} .caption h5 {
          font-size: 1rem;
        }
        
        .carousel-${carouselId} .caption p {
          font-size: 0.85rem;
        }
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [carouselId, galleryConfig.transitionDuration, galleryConfig.imageHeight, galleryConfig.transitionEffect, galleryConfig.backgroundColor, galleryConfig.borderRadius]);
  
  // Déterminer la taille de conteneur avec styles CSS
  const getContainerStyle = () => {
    // ✅ GESTION DU RESPONSIVE
    if (galleryConfig.responsive !== false) { // Par défaut true
      switch (galleryConfig.gallerySize) {
        case 'small': return { width: '100%', maxWidth: '400px', margin: '0 auto' };
        case 'medium': return { width: '100%', maxWidth: '800px', margin: '0 auto' };
        case 'large': return { width: '100%' };
        default: return { width: '100%', maxWidth: '800px', margin: '0 auto' };
      }
    } else {
      // Mode legacy (fixe)
      switch (galleryConfig.gallerySize) {
        case 'small': return { width: '25%', margin: '0 auto' };
        case 'medium': return { width: '50%', margin: '0 auto' };
        case 'large': return { width: '100%' };
        default: return { width: '50%', margin: '0 auto' };
      }
    }
  };
  
  // Protection contre les configurations incomplètes (après tous les hooks)
  if (!galleryConfig || typeof galleryConfig !== 'object') {
    return (
      <div className="alert alert-danger">
        {/* ✅ Traduction des messages d'erreur */}
        <h6>🚫 <AutoTranslateText sourceLang="fr">Erreur de configuration</AutoTranslateText></h6>
        <p><AutoTranslateText sourceLang="fr">La configuration du plugin est invalide.</AutoTranslateText></p>
      </div>
    );
  }
  
  if (!galleryConfig.images || galleryConfig.images.length === 0) {
    return (
      <div className="alert alert-info">
        {/* ✅ Traduction des messages d'information */}
        <h6>🖼️ <AutoTranslateText sourceLang="fr">Galerie d'images vide</AutoTranslateText></h6>
        <p><AutoTranslateText sourceLang="fr">Aucune image n'a été ajoutée à cette galerie.</AutoTranslateText></p>
      </div>
    );
  }
  
  return (
    <div className="image-gallery-plugin" style={{
      ...getContainerStyle(),
      backgroundColor: (galleryConfig.backgroundColor && galleryConfig.backgroundColor !== '') ? galleryConfig.backgroundColor : 'transparent',
      // ✅ APPLICATION DU STYLE AU CONTENEUR PRINCIPAL
      borderRadius: `${galleryConfig.borderRadius !== undefined ? galleryConfig.borderRadius : 8}px`,
      padding: (galleryConfig.backgroundColor && galleryConfig.backgroundColor !== 'transparent') ? '15px' : '0',
      overflow: 'hidden' // Important pour que le border-radius coupe le contenu
    }}>
      {/* ✅ APPLICATION DES STYLES GLOBAUX AU TITRE ET DESCRIPTION */}
      {(galleryConfig.showTitle !== false) && galleryConfig.title && (
        <h3 className="mb-3" style={{
           textAlign: galleryConfig.titleGlobalStyle?.textAlign || 'left',
           fontWeight: galleryConfig.titleGlobalStyle?.fontWeight || 'bold', // Gras par défaut pour h3
           fontStyle: galleryConfig.titleGlobalStyle?.fontStyle || 'normal',
           textDecoration: galleryConfig.titleGlobalStyle?.textDecoration || 'none',
           fontFamily: galleryConfig.titleGlobalStyle?.fontFamily || 'inherit',
           fontSize: galleryConfig.titleGlobalStyle?.fontSize ? `${galleryConfig.titleGlobalStyle.fontSize}px` : undefined,
           color: galleryConfig.titleGlobalStyle?.color || 'inherit',
           marginTop: 0
        }}>{galleryConfig.title}</h3>
      )}
      
      {(galleryConfig.showDescription !== false) && galleryConfig.description && (
        <p className="mb-3" style={{
           textAlign: galleryConfig.descriptionGlobalStyle?.textAlign || 'left',
           fontWeight: galleryConfig.descriptionGlobalStyle?.fontWeight || 'normal',
           fontStyle: galleryConfig.descriptionGlobalStyle?.fontStyle || 'normal',
           textDecoration: galleryConfig.descriptionGlobalStyle?.textDecoration || 'none',
           fontFamily: galleryConfig.descriptionGlobalStyle?.fontFamily || 'inherit',
           fontSize: galleryConfig.descriptionGlobalStyle?.fontSize ? `${galleryConfig.descriptionGlobalStyle.fontSize}px` : undefined,
           color: galleryConfig.descriptionGlobalStyle?.color || 'inherit'
        }}>{galleryConfig.description}</p>
      )}

      {galleryConfig.showDisplayModeSelector && (
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <span className="small text-muted">
            <AutoTranslateText sourceLang="fr">Mode d'affichage</AutoTranslateText>
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: '220px' }}
            value={activeDisplayMode}
            onChange={(e) => setDisplayMode(e.target.value as ImageGalleryConfig['displayMode'])}
          >
            <option value="grid">▦ Grille (Classique)</option>
            <option value="carousel">⏯️ Carrousel (Slider)</option>
            <option value="masonry">🧱 Mosaïque (Pinterest)</option>
          </select>
        </div>
      )}
      
      {activeDisplayMode === 'carousel' && (
        <div className={`carousel-${carouselId} effect-${galleryConfig.transitionEffect}`}>
          <div className="slide-container">  
            {galleryConfig.images.map((image, index) => (
              <div 
                key={index} 
                className={`slide ${index === currentSlide ? 'active' : ''} ${index === currentSlide - 1 || (currentSlide === 0 && index === galleryConfig.images.length - 1) ? 'prev' : ''}`}
              >
                <img
                  src={image.url}
                  alt={image.alt || image.title}
                />
                
                {(galleryConfig.showTitles || galleryConfig.showDescriptions) && (image.title || image.description) && (
                  <div className="caption">
                    {galleryConfig.showTitles && image.title && (
                      <h5 style={{
                        ...image.titleStyle,
                        fontFamily: image.titleStyle?.fontFamily || 'inherit',
                        fontSize: image.titleStyle?.fontSize ? `${image.titleStyle.fontSize}px` : 'inherit',
                        color: image.titleStyle?.color || 'inherit'
                      }}>{image.title}</h5>
                    )}
                    {galleryConfig.showDescriptions && image.description && (
                      <p style={{
                        ...image.descriptionStyle,
                        fontFamily: image.descriptionStyle?.fontFamily || 'inherit',
                        fontSize: image.descriptionStyle?.fontSize ? `${image.descriptionStyle.fontSize}px` : 'inherit',
                        color: image.descriptionStyle?.color || 'inherit'
                      }}>{image.description}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Contrôles de navigation */}
          {galleryConfig.images.length > 1 && (
            <>
              <button 
                className="nav-btn prev" 
                onClick={goToPrev}
                disabled={isTransitioning}
                aria-label="Image précédente"
              >
                &#8249;
              </button>
              <button 
                className="nav-btn next" 
                onClick={goToNext}
                disabled={isTransitioning}
                aria-label="Image suivante"
              >
                &#8250;
              </button>
            </>
          )}
          
          {/* Indicateurs */}
          {galleryConfig.images.length > 1 && (galleryConfig.showIndicators !== false) && (
            <div className="dots">
              {galleryConfig.images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={index === currentSlide ? 'active' : ''}
                  disabled={isTransitioning}
                  aria-label={`Aller à l'image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Mode grille */}
      {activeDisplayMode === 'grid' && (
        <div className="row">
          {galleryConfig.images.map((image, index) => (
            <div key={index} className={`col-md-${12 / (galleryConfig.columns || 3)} mb-3`}>
              <div className="card h-100">
                <img
                  src={image.url}
                  alt={image.alt || image.title}
                  className="card-img-top"
                  style={{ 
                    height: `${galleryConfig.imageHeight || 300}px`, 
                    objectFit: 'cover'
                  }}
                />
                {(galleryConfig.showTitles || galleryConfig.showDescriptions) && (
                  <div className="card-body">
                    {galleryConfig.showTitles && image.title && (
                      <h5 className="card-title" style={{
                        ...image.titleStyle,
                        fontFamily: image.titleStyle?.fontFamily || 'inherit',
                        fontSize: image.titleStyle?.fontSize ? `${image.titleStyle.fontSize}px` : 'inherit',
                        color: image.titleStyle?.color || 'inherit'
                      }}>{image.title}</h5>
                    )}
                    {galleryConfig.showDescriptions && image.description && (
                      <p className="card-text" style={{
                        ...image.descriptionStyle,
                        fontFamily: image.descriptionStyle?.fontFamily || 'inherit',
                        fontSize: image.descriptionStyle?.fontSize ? `${image.descriptionStyle.fontSize}px` : 'inherit',
                        color: image.descriptionStyle?.color || 'inherit'
                      }}>{image.description}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Mode mosaïque */}
      {activeDisplayMode === 'masonry' && (
        <div style={{ columns: galleryConfig.columns || 3, columnGap: '1rem' }}>
          {galleryConfig.images.map((image, index) => (
            <div key={index} className="d-inline-block w-100 mb-3">
              <div className="card">
                <img
                  src={image.url}
                  alt={image.alt || image.title}
                  className="card-img-top"
                  style={{ width: '100%', height: 'auto' }}
                />
                {(galleryConfig.showTitles || galleryConfig.showDescriptions) && (
                  <div className="card-body">
                    {galleryConfig.showTitles && image.title && (
                      <h5 className="card-title" style={{
                        ...image.titleStyle,
                        fontFamily: image.titleStyle?.fontFamily || 'inherit',
                        fontSize: image.titleStyle?.fontSize ? `${image.titleStyle.fontSize}px` : 'inherit',
                        color: image.titleStyle?.color || 'inherit'
                      }}>{image.title}</h5>
                    )}
                    {galleryConfig.showDescriptions && image.description && (
                      <p className="card-text" style={{
                        ...image.descriptionStyle,
                        fontFamily: image.descriptionStyle?.fontFamily || 'inherit',
                        fontSize: image.descriptionStyle?.fontSize ? `${image.descriptionStyle.fontSize}px` : 'inherit',
                        color: image.descriptionStyle?.color || 'inherit'
                      }}>{image.description}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Configuration du plugin
const ImageGalleryConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const galleryConfig = config as ImageGalleryConfig;
  const [activeTab, setActiveTab] = useState<'general' | 'images'>('general');
  const [generalField, setGeneralField] = useState<'title' | 'description'>('title');
  const { apiKey: tinymceApiKey, isLoading: tinymceLoading } = useTinyMceApiKey();
  
  // Remplacer useFlmngrUtils par useFileManager
  const { openFileManager } = useFileManager();

  const updateGeneralFieldValue = (value: string) => {
    if (generalField === 'title') {
      onConfigChange({ ...galleryConfig, title: value });
      return;
    }
    onConfigChange({ ...galleryConfig, description: value });
  };

  const updateGeneralFieldStyle = (newStyle: any) => {
    if (generalField === 'title') {
      onConfigChange({ ...galleryConfig, titleGlobalStyle: newStyle });
      return;
    }
    onConfigChange({ ...galleryConfig, descriptionGlobalStyle: newStyle });
  };

  const openFlmngr = () => {
    // Utiliser le nouveau gestionnaire de fichiers
    openFileManager({
      type: 'image',
      onFileSelect: (file) => {
        if (file && file.url) {
          const newImage = {
            url: file.url,
            title: 'Image',
            description: '',
            alt: 'Image de la galerie'
          };
          
          // Ajouter la nouvelle image à la liste existante
          onConfigChange({
            ...galleryConfig,
            images: [...(galleryConfig.images || []), newImage]
          });
        }
      }
    });
  };

  const loadExampleGallery = () => {
    // Utiliser API_CONFIG pour construire les URLs dynamiquement
    const baseUrl = API_CONFIG.getBaseUrl();
    
    const exampleImages = [
      {
        url: `${baseUrl}/uploads/IMG_0879.jpg`,
        title: 'Image d\'exemple 1',
        description: 'Description de la première image d\'exemple',
        alt: 'Première image d\'exemple'
      },
      {
        url: `${baseUrl}/uploads/IMG_1224.jpg`,
        title: 'Image d\'exemple 2',
        description: 'Description de la deuxième image d\'exemple',
        alt: 'Deuxième image d\'exemple'
      },
      {
        url: `${baseUrl}/uploads/IMG_1360.jpg`,
        title: 'Image d\'exemple 3',
        description: 'Description de la troisième image d\'exemple',
        alt: 'Troisième image d\'exemple'
      }
    ];
    
    onConfigChange({
      ...galleryConfig,
      title: 'Galerie d\'exemple',
      description: 'Cette galerie contient des images d\'exemple pour vous montrer les possibilités.',
      images: exampleImages,
      displayMode: 'carousel',
      showTitles: true,
      showDescriptions: true,
      autoplay: true,
      autoplayDelay: 4000,
      transitionEffect: 'fade',
      gallerySize: 'medium'
    });
  };

  return (
    <div className="p-3">
      {/* ✅ Système d'onglets */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            ⚙️ <AutoTranslateText sourceLang="fr">Général</AutoTranslateText>
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'images' ? 'active' : ''}`}
            onClick={() => setActiveTab('images')}
          >
            🖼️ <AutoTranslateText sourceLang="fr">Images</AutoTranslateText> ({galleryConfig.images?.length || 0})
          </button>
        </li>
      </ul>

      <div className="tab-content">
        {/* ================================================================================= */}
        {/*                               ONGLET GÉNÉRAL                                      */}
        {/* ================================================================================= */}
        {activeTab === 'general' && (
          <div className="animate-fade-in">
            {/* Titre et description avec BARRE D'OUTILS */}
            <div className="mb-3">
              <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Champ global</AutoTranslateText></label>
              <select
                className="form-select"
                value={generalField}
                onChange={(e) => setGeneralField(e.target.value as 'title' | 'description')}
              >
                <option value="title">Titre global</option>
                <option value="description">Description globale</option>
              </select>
            </div>
            
            <div className="mb-3">
              <label className="form-label fw-bold">
                <AutoTranslateText sourceLang="fr">
                  {generalField === 'title' ? 'Titre global' : 'Description globale'}
                </AutoTranslateText>
              </label>
              {tinymceLoading ? (
                <div>Chargement...</div>
              ) : !tinymceApiKey ? (
                <textarea
                  className="form-control mb-1"
                  rows={3}
                  value={generalField === 'title' ? (galleryConfig.title || '') : (galleryConfig.description || '')}
                  onChange={(e) => updateGeneralFieldValue(e.target.value)}
                />
              ) : (
                <Editor
                  apiKey={tinymceApiKey}
                  value={generalField === 'title' ? (galleryConfig.title || '') : (galleryConfig.description || '')}
                  onEditorChange={(content) => updateGeneralFieldValue(content)}
                  init={TINYMCE_TEXT_INIT}
                />
              )}
              <TextFormattingToolbar
                style={generalField === 'title' ? (galleryConfig.titleGlobalStyle || {}) : (galleryConfig.descriptionGlobalStyle || {})}
                onStyleChange={updateGeneralFieldStyle}
                label={generalField === 'title' ? 'Style du Titre' : 'Style de la Description'}
              />
            </div>
            
            {/* Options d'affichage du titre et description */}
            <div className="row mb-3 bg-light p-2 rounded mx-1">
              <div className="col-md-4">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={galleryConfig.showTitle !== false}
                    onChange={(e) => onConfigChange({ ...galleryConfig, showTitle: e.target.checked })}
                  />
                  <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Afficher le titre</AutoTranslateText></label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={galleryConfig.showDescription !== false}
                    onChange={(e) => onConfigChange({ ...galleryConfig, showDescription: e.target.checked })}
                  />
                  <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Afficher la description</AutoTranslateText></label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={galleryConfig.showIndicators !== false}
                    onChange={(e) => onConfigChange({ ...galleryConfig, showIndicators: e.target.checked })}
                  />
                  <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Pagination (Points)</AutoTranslateText></label>
                </div>
              </div>
            </div>
            
            <hr />

            {/* Mode d'affichage */}
            <div className="mb-3">
              <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Mode d'affichage</AutoTranslateText></label>
              <select
                className="form-select"
                value={galleryConfig.displayMode || 'grid'}
                onChange={(e) => onConfigChange({ ...galleryConfig, displayMode: e.target.value as any })}
              >
                <option value="grid">▦ Grille (Classique)</option>
                <option value="carousel">⏯️ Carrousel (Slider)</option>
                <option value="masonry">🧱 Mosaïque (Pinterest)</option>
              </select>
            </div>

            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                checked={galleryConfig.showDisplayModeSelector || false}
                onChange={(e) => onConfigChange({ ...galleryConfig, showDisplayModeSelector: e.target.checked })}
              />
              <label className="form-check-label">
                <AutoTranslateText sourceLang="fr">
                  Autoriser le visiteur à changer le mode d'affichage
                </AutoTranslateText>
              </label>
            </div>
            
            {/* Configuration du carousel */}
            {galleryConfig.displayMode === 'carousel' && (
              <div className="card mb-3 bg-light border-0">
                <div className="card-body py-2">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small"><AutoTranslateText sourceLang="fr">Effet</AutoTranslateText></label>
                      <select
                        className="form-select form-select-sm"
                        value={galleryConfig.transitionEffect || 'fade'}
                        onChange={(e) => onConfigChange({ ...galleryConfig, transitionEffect: e.target.value as any })}
                      >
                        <option value="fade">Fondu</option>
                        <option value="slide">Glissement</option>
                        <option value="zoom">Zoom</option>
                        <option value="flip">Rotation</option>
                        <option value="cube">Cube</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small"><AutoTranslateText sourceLang="fr">Durée (ms)</AutoTranslateText></label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={galleryConfig.transitionDuration || 1000}
                        onChange={(e) => onConfigChange({ ...galleryConfig, transitionDuration: parseInt(e.target.value) })}
                        min="100" max="3000" step="100"
                      />
                    </div>
                    <div className="col-12 mt-2">
                       <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={galleryConfig.autoplay || false}
                          onChange={(e) => onConfigChange({ ...galleryConfig, autoplay: e.target.checked })}
                        />
                        <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Lecture automatique</AutoTranslateText></label>
                      </div>
                    </div>
                    {galleryConfig.autoplay && (
                      <div className="col-12">
                         <div className="input-group input-group-sm">
                           <span className="input-group-text">Délai</span>
                           <input
                              type="number"
                              className="form-control"
                              value={galleryConfig.autoplayDelay || 3000}
                              onChange={(e) => onConfigChange({ ...galleryConfig, autoplayDelay: parseInt(e.target.value) })}
                              min="1000" max="10000" step="500"
                            />
                            <span className="input-group-text">ms</span>
                         </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="row mb-3">
              {/* Taille de la galerie */}
              <div className="col-md-6">
                 <label className="form-label small"><AutoTranslateText sourceLang="fr">Largeur galerie</AutoTranslateText></label>
                  <select
                    className="form-select form-select-sm"
                    value={galleryConfig.gallerySize || 'medium'}
                    onChange={(e) => onConfigChange({ ...galleryConfig, gallerySize: e.target.value as any })}
                  >
                    <option value="small">Petite (25%)</option>
                    <option value="medium">Moyenne (50%)</option>
                    <option value="large">Grande (100%)</option>
                  </select>
              </div>
              
              {/* Hauteur des images */}
              <div className="col-md-6">
                 <label className="form-label small"><AutoTranslateText sourceLang="fr">Hauteur images (px)</AutoTranslateText></label>
                 <input
                    type="number"
                    className="form-control form-control-sm"
                    value={galleryConfig.imageHeight || 400}
                    onChange={(e) => onConfigChange({ ...galleryConfig, imageHeight: parseInt(e.target.value) })}
                    min="100" max="1000" step="50"
                  />
              </div>
            </div>
            
            {/* Nombre de colonnes pour grille/mosaïque */}
            {(galleryConfig.displayMode === 'grid' || galleryConfig.displayMode === 'masonry') && (
              <div className="mb-3">
                <label className="form-label small"><AutoTranslateText sourceLang="fr">Nombre de colonnes</AutoTranslateText></label>
                <div className="btn-group w-100" role="group">
                  {[1, 2, 3, 4, 6].map(num => (
                    <button
                      key={num}
                      type="button"
                      className={`btn btn-sm ${galleryConfig.columns === num ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => onConfigChange({ ...galleryConfig, columns: num })}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <hr />
            <h6>🎨 <AutoTranslateText sourceLang="fr">Apparence</AutoTranslateText></h6>

            {/* Responsive & Arrondi */}
            <div className="row mb-3">
              <div className="col-md-6">
                  <div className="form-check form-switch pt-2">
                      <input
                          type="checkbox"
                          className="form-check-input"
                          id="responsiveSwitch"
                          checked={galleryConfig.responsive !== false}
                          onChange={(e) => onConfigChange({ ...galleryConfig, responsive: e.target.checked })}
                      />
                      <label className="form-check-label small" htmlFor="responsiveSwitch">
                          <AutoTranslateText sourceLang="fr">100% Mobile</AutoTranslateText>
                      </label>
                  </div>
              </div>
              <div className="col-md-6">
                  <label className="form-label mb-0 small">
                      <AutoTranslateText sourceLang="fr">Arrondi</AutoTranslateText>: {galleryConfig.borderRadius || 0}px
                  </label>
                  <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="30"
                      step="1"
                      value={galleryConfig.borderRadius || 0}
                      onChange={(e) => onConfigChange({ ...galleryConfig, borderRadius: parseInt(e.target.value) })}
                  />
              </div>
            </div>

            {/* Couleur de fond */}
            <div className="mb-3">
              <label className="form-label small"><AutoTranslateText sourceLang="fr">Couleur de fond</AutoTranslateText></label>
              
              <div className="d-flex align-items-center gap-2 mb-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={galleryConfig.backgroundColor && galleryConfig.backgroundColor !== 'transparent' ? galleryConfig.backgroundColor : '#ffffff'}
                  onChange={(e) => onConfigChange({ ...galleryConfig, backgroundColor: e.target.value })}
                  style={{ width: '40px', height: '30px' }}
                  disabled={galleryConfig.backgroundColor === 'transparent'}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={galleryConfig.backgroundColor || ''}
                  onChange={(e) => onConfigChange({ ...galleryConfig, backgroundColor: e.target.value })}
                  placeholder="transparent"
                />
              </div>

              <div className="d-flex gap-1 justify-content-between">
                <button
                  type="button"
                  className={`btn btn-sm flex-grow-1 ${!galleryConfig.backgroundColor || galleryConfig.backgroundColor === 'transparent' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => onConfigChange({ ...galleryConfig, backgroundColor: 'transparent' })}
                >
                  🔳 Transp.
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-grow-1 ${galleryConfig.backgroundColor === '#ffffff' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => onConfigChange({ ...galleryConfig, backgroundColor: '#ffffff' })}
                >
                  ⚪ Blanc
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-grow-1 ${galleryConfig.backgroundColor === '#f8f9fa' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => onConfigChange({ ...galleryConfig, backgroundColor: '#f8f9fa' })}
                >
                  🌫️ Gris
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-grow-1 ${galleryConfig.backgroundColor === '#000000' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => onConfigChange({ ...galleryConfig, backgroundColor: '#000000' })}
                >
                  ⚫ Noir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================================= */}
        {/*                                 ONGLET IMAGES                                     */}
        {/* ================================================================================= */}
        {activeTab === 'images' && (
          <div className="animate-fade-in">
             <div className="d-flex justify-content-between align-items-center mb-3">
               <div>
                  <h6 className="mb-0">Gestion des médias</h6>
                  <small className="text-muted">{galleryConfig.images?.length || 0} images ajoutées</small>
               </div>
               <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={loadExampleGallery}>
                  🖼️ <AutoTranslateText sourceLang="fr">Exemple</AutoTranslateText>
                </button>
                <button className="btn btn-sm btn-primary" onClick={openFlmngr}>
                  ➕ <AutoTranslateText sourceLang="fr">Ajouter</AutoTranslateText>
                </button>
               </div>
             </div>
             
             {/* ✅ SCROLL CONTAINER : Zone de défilement vertical */}
             <div style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '5px' }}>
                 {/* Liste des images */
                 galleryConfig.images && galleryConfig.images.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {galleryConfig.images.map((image, index) => (
                        // ✅ MODIFICATION LAYOUT : Card verticale
                        <div key={index} className="card border shadow-sm">
                           <div className="card-body p-3">
                              
                              {/* LIGNE 1 : Image Preview & Suppression */}
                              <div className="position-relative mb-3 text-center bg-light rounded border p-2" 
                                   style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                   <img 
                                     src={image.url} 
                                     alt={image.title} 
                                     className="img-fluid rounded"
                                     style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                    <button
                                      className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 shadow-sm d-flex align-items-center gap-1"
                                      onClick={() => {
                                        const newImages = galleryConfig.images.filter((_, i) => i !== index);
                                        onConfigChange({ ...galleryConfig, images: newImages });
                                      }}
                                      title="Supprimer cette image"
                                    >
                                      <span style={{ fontSize: '1.2em', lineHeight: 1 }}>×</span> Supprimer
                                    </button>
                              </div>
                              
                              {/* LIGNE 2 : Partie Setup (Formulaires) */}
                              <div className="mt-3">
                                 {/* Titre */}
                                 <div className="mb-3">
                                   <label className="form-label small fw-bold mb-1">Titre de l'image</label>
                                   <div className="input-group input-group-sm">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Titre..."
                                        value={image.title}
                                        onChange={(e) => {
                                          const newImages = [...galleryConfig.images];
                                          newImages[index] = { ...newImages[index], title: e.target.value };
                                          onConfigChange({ ...galleryConfig, images: newImages });
                                        }}
                                      />
                                   </div>
                                   <TextFormattingToolbar
                                      style={image.titleStyle || {}}
                                      onStyleChange={(newStyle) => {
                                        const newImages = [...galleryConfig.images];
                                        newImages[index] = { ...newImages[index], titleStyle: newStyle };
                                        onConfigChange({ ...galleryConfig, images: newImages });
                                      }}
                                      label="Style du titre"
                                    />
                                 </div>
                                 
                                 {/* Description */}
                                 <div className="mb-3">
                                    <label className="form-label small fw-bold mb-1">Description</label>
                                    <textarea
                                      className="form-control form-control-sm mb-1"
                                      placeholder="Description détaillée..."
                                      rows={2}
                                      value={image.description || ''}
                                      onChange={(e) => {
                                        const newImages = [...galleryConfig.images];
                                        newImages[index] = { ...newImages[index], description: e.target.value };
                                        onConfigChange({ ...galleryConfig, images: newImages });
                                      }}
                                    />
                                    <TextFormattingToolbar
                                        style={image.descriptionStyle || {}}
                                        onStyleChange={(newStyle) => {
                                          const newImages = [...galleryConfig.images];
                                          newImages[index] = { ...newImages[index], descriptionStyle: newStyle };
                                          onConfigChange({ ...galleryConfig, images: newImages });
                                        }}
                                        label="Style de la description"
                                      />
                                 </div>
                                 
                                 {/* Alt Text */}
                                 <div>
                                    <label className="form-label small text-muted mb-0">Texte Alt (SEO)</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Description pour les moteurs de recherche"
                                      value={image.alt || ''}
                                      onChange={(e) => {
                                        const newImages = [...galleryConfig.images];
                                        newImages[index] = { ...newImages[index], alt: e.target.value };
                                        onConfigChange({ ...galleryConfig, images: newImages });
                                      }}
                                    />
                                 </div>
                              </div>

                           </div>
                        </div>
                      ))}
                    </div>
                 ) : (
                   <div className="text-center p-5 text-muted border border-dashed rounded bg-light">
                     <div className="fs-1 mb-2">📸</div>
                     <p>Aucune image pour le moment.</p>
                     <button className="btn btn-sm btn-primary" onClick={openFlmngr}>
                       Commencer à ajouter
                     </button>
                   </div>
                 )}
             </div>
             
             <div className="alert alert-info py-2 mt-3 mb-0">
               <div className="d-flex align-items-center small">
                 <div className="flex-fill">
                    <div className="form-check form-check-inline">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={galleryConfig.showTitles || false}
                        onChange={(e) => onConfigChange({ ...galleryConfig, showTitles: e.target.checked })}
                      />
                      <label className="form-check-label">Afficher titres</label>
                    </div>
                 </div>
                 <div className="flex-fill">
                    <div className="form-check form-check-inline">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={galleryConfig.showDescriptions || false}
                        onChange={(e) => onConfigChange({ ...galleryConfig, showDescriptions: e.target.checked })}
                      />
                      <label className="form-check-label">Afficher descriptions</label>
                    </div>
                 </div>
               </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Export du plugin modulaire - CONFORME À L'INTERFACE MODULAIRE
const ImageGalleryPluginModular: PluginInterface = {
  metadata:
   {
    id: 'image-gallery-modular',
    name: "Galerie d'images",
    description: "Plugin modulaire pour créer des galeries d'images avec différents modes d'affichage et effets de transition",
    version: '2.0.0',
    author: 'Book Editor',
    category: 'media',
    icon: '🖼️',
    tags: ['images', 'galerie', 'carousel', 'médias']
  },

  // ✅ DOCUMENTATION ENRICHIE POUR L'IA (ContainerWizard)
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Galerie d'images polyvalente. Supporte : Carrousel (Slider) avec effets, Grille classique, ou Mosaïque (Masonry).",
    usage: "Utilise ce plugin pour afficher un portfolio, un album photo, des diapositives de présentation ou une série d'images.",
    
    configSchema: {
      type: "object",
      properties: {
        title: { type: "string" },
        description: { type: "string" },
        
        // Mode et Disposition
        displayMode: { type: "string", enum: ["carousel", "grid", "masonry"], default: "grid" },
        showDisplayModeSelector: { type: "boolean", default: false, description: "Autoriser le visiteur à changer le mode d'affichage" },
        columns: { type: "number", enum: [1, 2, 3, 4, 6], description: "Seulement pour grid/masonry" },
        gallerySize: { type: "string", enum: ["small", "medium", "large"], default: "medium" },
        imageHeight: { type: "number", description: "Hauteur en pixels (ex: 400)" },
        
        // Options Carrousel
        autoplay: { type: "boolean" },
        transitionEffect: { type: "string", enum: ["fade", "slide", "zoom", "flip", "cube"] },
        
        // Apparence
        borderRadius: { type: "number", description: "Arrondi des bords (0-30)" },
        backgroundColor: { type: "string (hex)", description: "Couleur ou 'transparent'" },
        responsive: { type: "boolean", default: true, description: "Adapter à 100% sur mobile" },
        
        // Contenu
        images: {
          type: "array",
          items: {
            type: "object",
            properties: {
              url: { type: "string", description: "URL absolue de l'image" },
              title: { type: "string" },
              description: { type: "string" },
              alt: { type: "string" }
            },
            required: ["url"]
          }
        }
      }
    } as any,

    exampleConfig: {
      title: "Portfolio Nature",
      description: "Nos plus beaux clichés de la saison.",
      displayMode: "carousel",
      showDisplayModeSelector: true,
      transitionEffect: "fade",
      autoplay: true,
      imageHeight: 500,
      borderRadius: 12,
      backgroundColor: "transparent",
      responsive: true,
      images: [
        { 
          url: "https://images.unsplash.com/photo-1472214103451-9374bd1c798e", 
          title: "Montagne",
          description: "Vue matinale" 
        },
        { 
          url: "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d", 
          title: "Forêt",
          description: "Automne 2024" 
        }
      ]
    }
  },

  defaultConfig: {
    title: 'Ma galerie',
    description: '',
    images: [],
    displayMode: 'carousel',
    showDisplayModeSelector: false,
    columns: 3,
    showTitles: true,
    showDescriptions: true,
    lightbox: false,
    gallerySize: 'medium',
    imageHeight: 400,
    transitionEffect: 'fade',
    transitionDuration: 1000,
    autoplay: true,
    autoplayDelay: 3000,
    backgroundColor: 'transparent', // ✅ Transparent par défaut
    borderRadius: 8,                // ✅ Arrondi par défaut
    responsive: true,               // ✅ Responsive par défaut
    showTitle: true,
    showDescription: true,
    showIndicators: true,
  },

  ConfigComponent: ImageGalleryConfigComponent,
  PreviewComponent: ImageGalleryRenderer,

  // ✅ CORRECTION MAJEURE : Utilisation de generateUniformPlaceholder avec ID unique
  generateHTML: (config: ImageGalleryConfig) => {
    // 1. Génération de l'ID unique
    const instanceId = config.id || `gallery-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    // 2. On sauvegarde l'ID dans la config
    const configWithId = { ...config, id: instanceId };

    const imageCount = config.images?.length || 0;
    const modeLabel = config.displayMode === 'carousel' ? 'Carrousel' : 
                      config.displayMode === 'masonry' ? 'Mosaïque' : 'Grille';

    return generateUniformPlaceholder({
      // ✅ C'est ici que ça se joue : ID unique pour l'édition
      pluginId: instanceId,
      pluginType: 'image-gallery-modular',

      title: config.title || 'Galerie Photo',
      description: `🖼️ ${imageCount} image(s) • Mode ${modeLabel}`,
      icon: '🖼️',
      category: 'media',

      // ✅ On passe la config enrichie avec l'ID
      config: configWithId
    });
  }
};

export default ImageGalleryPluginModular;
